/* Adapt provider markup without replacing its results, native inputs or links. */
(() => {
  'use strict';
  const page = document.querySelector('.dac-search-page');
  if (!page) return;
  const copy = JSON.parse(page.dataset.layoutCopy || '{}');
  let scheduled = false;
  let collapsed = false;
  let modal = null;
  let modalElement = null;
  let stockFocusElement = null;
  const searchLocation = () => window.DACSearchQueryDisplay?.locationKey?.() || location.pathname + location.search;
  let resultsLocation = searchLocation();
  let providerStartedMobile = null;
  let reinitializingProvider = false;
  const desktop = matchMedia('(min-width:1280px)');
  const template = document.querySelector('#dac-search-filter-heading');
  const retryForm = page.querySelector('.dac-search-retry');
  function normalizeResultCopy(root) {
    root.querySelectorAll('.snize-search-results-header').forEach(header => {
      const count = header.querySelector(':scope > b');
      const value = count?.textContent.trim();
      if (value && /^[0-9][0-9,.\s]*$/.test(value)) {
        const label = value.replace(/[,\.\s]/g, '') === '1' ? copy.resultsOne : copy.resultsMany;
        const parts = label.split('__COUNT__');
        const expected = parts[0] + value + parts[1];
        if (header.textContent !== expected || header.querySelector('a')) {
          header.replaceChildren(document.createTextNode(parts[0]), count, document.createTextNode(parts[1]));
        }
      } else if (header.textContent !== copy.resultsTitle) {
        header.textContent = copy.resultsTitle;
      }
      header.setAttribute('data-dac-query-free', '');
    });
    root.querySelectorAll('.snize-no-products-found-text').forEach(message => {
      const browse = message.querySelector('a.snize-link-home');
      const messageCopy = message.closest('.snize-with-suggestion') ? copy.similarResults : copy.noResults;
      const expected = messageCopy + (browse ? ' ' + copy.browseStore : '');
      if (message.textContent !== expected || message.querySelector('b')) {
        if (browse) browse.textContent = copy.browseStore;
        message.replaceChildren(document.createTextNode(messageCopy + (browse ? ' ' : '')), ...(browse ? [browse] : []));
      }
      message.setAttribute('data-dac-query-free', '');
    });
  }
  const picks = page.querySelector('.dac-search-bestsellers');
  const track = picks?.querySelector('.dac-search-bestsellers__track');
  function syncPicks() {
    if (!track || !track.clientWidth) return;
    const max = track.scrollWidth - track.clientWidth;
    picks.querySelector('.dac-search-bestsellers__controls').hidden = max < 2;
    picks.querySelectorAll('[data-picks-direction]').forEach(button => {
      const atEdge = Number(button.dataset.picksDirection) < 0 ? track.scrollLeft < 2 : track.scrollLeft >= max - 2;
      button.setAttribute('aria-disabled', String(atEdge));
    });
  }
  if (track) {
    picks.addEventListener('click', event => {
      const button = event.target.closest('[data-picks-direction]');
      if (!button || button.getAttribute('aria-disabled') === 'true') return;
      const step = track.firstElementChild.getBoundingClientRect().width + parseFloat(getComputedStyle(track).gap);
      track.scrollBy({left: Number(button.dataset.picksDirection) * step, behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    });
    track.addEventListener('scroll', syncPicks, {passive:true});
    window.addEventListener('resize', syncPicks, {passive:true});
  }
  const recovery = page.querySelector('.dac-search-recovery');
  let recoveryQuery = null;
  function nearbyWord(a, b) {
    if (a === b) return true;
    if (a.length < 5 || b.length < 5 || Math.abs(a.length - b.length) > 1) return false;
    let i = 0, j = 0, edits = 0;
    while (i < a.length && j < b.length) {
      if (a[i] === b[j]) { i++; j++; continue; }
      if (++edits > 1) return false;
      if (a.length >= b.length) i++;
      if (b.length >= a.length) j++;
    }
    return edits + (a.length - i) + (b.length - j) <= 1;
  }
  function currentQuery() {
    const query = document.querySelector('#dac-header-search')?.value || window.DACSearchQueryDisplay?.query?.() || new URL(location.href).searchParams.get('q') || '';
    return window.DACSearchQueryDisplay?.value(query) ?? query;
  }
  function updateRecovery() {
    const query = currentQuery();
    const key = location.search + query;
    if (!recovery || recoveryQuery === key) return;
    recoveryQuery = key;
    const url = new URL(location.href);
    const terms = query.slice(0, 200).toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').split(/[^a-z0-9]+/).filter(Boolean).slice(0, 12);
    const topics = [...recovery.querySelectorAll('[data-search-topics]')];
    const matches = topics.filter(topic => topic.dataset.searchTopics.split(' ').some(word => terms.some(term => nearbyWord(term, word)))).slice(0, 4);
    topics.forEach(topic => { topic.hidden = matches.length ? !matches.includes(topic) : !topic.hasAttribute('data-default-topic'); });
    recovery.querySelector('.dac-search-recovery__suggestions')?.toggleAttribute('data-related', matches.length > 0);
    const heading = recovery.querySelector('[data-related-heading]');
    if (heading) heading.textContent = matches.length ? heading.dataset.relatedHeading : heading.dataset.defaultHeading;
    const reset = recovery.querySelector('.dac-search-recovery__reset');
    const filtered = [...url.searchParams.keys()].some(key => /^(rb_|filter[.\[])/.test(key));
    if (reset) {
      reset.hidden = !filtered;
      const destination = new URL(retryForm.action, location.href);
      destination.searchParams.set('q', query);
      const preview = selectedPreview();
      if (preview) destination.searchParams.set('preview_theme_id', preview);
      reset.href = destination.href;
    }
  }
  function selectedPreview() {
    return new URL(location.href).searchParams.get('preview_theme_id') ||
      (/unpublished|development/.test(window.Shopify?.theme?.role || '') ? String(window.Shopify.theme.id) : '');
  }
  function submitRetry(event) {
    event.preventDefault();
    event.stopImmediatePropagation();
    const destination = new URL(retryForm.action, location.href);
    new FormData(retryForm).forEach((value, key) => destination.searchParams.set(key, value));
    const preview = selectedPreview();
    if (preview) destination.searchParams.set('preview_theme_id', preview);
    location.assign(destination.href);
  }
  // Keep native search interception from dropping the selected theme preview.
  retryForm.addEventListener('submit', submitRetry, true);
  retryForm.querySelector('button').addEventListener('click', submitRetry, true);

  function closeFilters() {
    modalElement?.classList.remove('dac-search-filter-open');
    modal?.release();
    modal = null;
    modalElement = null;
    page.querySelector('.dac-search-sidebar-toggle')?.setAttribute('aria-expanded', String(desktop.matches && !collapsed));
  }

  function stockInput(root) {
    return root.querySelector('.snize-filters-sidebar input[name="stock_status"][value="In Stock"]') ||
      document.querySelector('#snize-modal-mobile-filters-dialog input[name="stock_status"][value="In Stock"]');
  }
  function syncStock(root) {
    const mobile = root.classList.contains('snize-mobile-design');
    const panel = root.querySelector('.snize-main-panel');
    const host = mobile ? panel : root.querySelector('.snize-horizontal-left');
    if (!host) return;
    let control = root.querySelector('.dac-search-stock-control');
    if (!control) {
      control = document.createElement('div');
      control.className = 'dac-stock-control dac-search-stock-control';
      control.append(document.getElementById('dac-search-stock-toggle').content.cloneNode(true));
    }
    if (control.parentElement !== host) host.append(control);
    const input = stockInput(root);
    const active = input ? input.checked : new URL(location.href).searchParams.getAll('rb_stock_status').includes('In Stock');
    const count = Number(input?.closest('li')?.querySelector('.snize-filter-variant-count')?.textContent.replace(/[^0-9]/g, '') || 0);
    const total = Number(root.querySelector('.snize-products-tab-total')?.textContent.replace(/[^0-9]/g, '') || 0);
    const otherTab = root.matches('.snize-tab-selected-categories,.snize-tab-selected-pages');
    control.hidden = otherTab || (!active && !(count > 0 && count < total));
    const toggle = control.querySelector('button');
    toggle.setAttribute('aria-pressed', String(active));
    toggle.disabled = !!input?.disabled && !active;
    if (stockFocusElement && !stockFocusElement.isConnected) {
      if (!control.hidden && (!document.activeElement || document.activeElement === document.body)) toggle.focus({preventScroll:true});
      stockFocusElement = null;
    }
    let summary = root.querySelector('.dac-search-mobile-summary');
    const views = (mobile && panel.querySelector('.snize-main-panel-view-mode')) || root.querySelector('.snize-main-panel-view-mode');
    const header = (mobile && root.querySelector(':scope > .snize-header')) || root.querySelector('.snize-header');
    if (mobile && views && header) {
      if (!summary) { summary = document.createElement('div'); summary.className = 'dac-search-mobile-summary'; }
      if (summary.parentElement !== panel) panel.append(summary);
      if (summary.children.length !== 2 || summary.children[0] !== header || summary.children[1] !== views) summary.replaceChildren(header, views);
    } else if (!mobile && summary) {
      if (views) root.querySelector('.snize-horizontal-right')?.prepend(views);
      if (header) host.append(header);
      summary.remove();
    }
  }

  function apply() {
    window.DACSearchQueryDisplay?.sync();
    if (document.title !== copy.pageTitle) document.title = copy.pageTitle;
    const root = document.getElementById('snize_results');
    if (!root) return;
    if (providerStartedMobile === null && root.classList.contains('snize-search-results')) {
      providerStartedMobile = root.classList.contains('snize-mobile-design');
    }
    // The provider retains its initial toolbar hosts and delegated handlers.
    // A mode change can lose desktop controls or leave duplicate mobile sorts.
    // Reinitialize only those broken transitions, once; intact resizes stay local.
    const mobileProvider = root.classList.contains('snize-mobile-design');
    const missingDesktop = providerStartedMobile && !mobileProvider && !root.querySelector('.snize-horizontal-left');
    const duplicateMobile = providerStartedMobile === false && mobileProvider &&
      root.querySelector('.snize-horizontal-right .snize-main-panel-dropdown') &&
      root.querySelector('.snize-main-panel .snize-main-panel-dropdown');
    if (!reinitializingProvider && root.querySelector('.snize-products-tab-total') && (missingDesktop || duplicateMobile)) {
      reinitializingProvider = true;
      location.reload();
      return;
    }
    normalizeResultCopy(root);
    if (modalElement && !modalElement.isConnected) closeFilters();
    root.classList.toggle('dac-search-sidebar-collapsed', collapsed && desktop.matches);
    const sidebar = root.querySelector(':scope > .snize-filters-sidebar');
    const tools = root.querySelector('.snize-horizontal-left');
    if (sidebar && !sidebar.querySelector('.dac-search-filter-heading') && template) {
      sidebar.prepend(template.content.cloneNode(true));
    }
    if (sidebar && tools && !tools.querySelector('.dac-search-sidebar-toggle')) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'dac-search-sidebar-toggle';
      button.addEventListener('click', () => {
        const current = document.querySelector('#snize_results > .snize-filters-sidebar');
        if (!current) return;
        if (desktop.matches) { collapsed = !collapsed; apply(); return; }
        current.classList.add('dac-search-filter-open');
        current.setAttribute('aria-label', copy.filters);
        modalElement = current;
        if (window.DACLibraryCards) modal = window.DACLibraryCards.openModal(current, button, {persistent:true,onClose:closeFilters});
        button.setAttribute('aria-expanded', 'true');
        current.querySelector('.dac-search-filter-close')?.focus();
      });
      tools.prepend(button);
    }
    const button = root.querySelector('.dac-search-sidebar-toggle');
    if (button) {
      button.textContent = desktop.matches ? (collapsed ? copy.show : copy.hide) : copy.filters;
      button.setAttribute('aria-expanded', String(desktop.matches ? !collapsed : !!modal));
    }
    root.querySelectorAll('.snize-main-panel-view-mode .snize-view-mode-icon').forEach(link => {
      if (link.querySelector('.dac-search-view-label')) return;
      const label = document.createElement('span');
      label.className = 'dac-search-view-label';
      label.textContent = link.classList.contains('snize-grid-mode-icon') ? copy.grid : link.classList.contains('snize-list-mode-icon') ? copy.list : (copy.row || "Rækker");
      link.append(label);
    });
    root.querySelectorAll('.snize-main-panel-dropdown > a').forEach(link => { link.dataset.dacSortLabel = copy.sort; });
    syncStock(root);
    const retry = page.querySelector('.dac-search-retry');
    const empty = root.querySelector('.snize-no-products-found:not(.snize-with-suggestion)');
    const isEmpty = !!empty;
    retry.hidden = !isEmpty;
    if (isEmpty) { updateRecovery(); syncPicks(); }
    if (isEmpty && document.activeElement !== retry.elements.q) {
      retry.elements.q.value = currentQuery();
    }
    const preview = selectedPreview();
    if (preview && !retry.querySelector('[name="preview_theme_id"]')) {
      const field = document.createElement('input'); field.type = 'hidden'; field.name = 'preview_theme_id'; field.value = preview; retry.append(field);
    }
    if (preview) page.querySelectorAll('.dac-collection-breadcrumbs a, #snize_results a[href], .dac-search-recovery a[href], .dac-search-bestsellers a[href]').forEach(link => {
      if (!link.getAttribute('href') || link.getAttribute('href').startsWith('#')) return;
      if (new URL(link.href).origin !== location.origin) return;
      const url = new URL(link.href); url.searchParams.set('preview_theme_id', preview); link.href = url.href;
    });
  }
  function schedule() {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => { scheduled = false; observer.disconnect(); apply(); observer.observe(page, {childList:true,subtree:true}); });
  }
  const observer = new MutationObserver(schedule);
  const title = document.querySelector('title');
  if (title) new MutationObserver(schedule).observe(title, {childList:true,subtree:true,characterData:true});
  page.addEventListener('click', event => {
    if (event.target.closest('.dac-search-filter-close')) closeFilters();
    const toggle = event.target.closest('[data-dac-stock-toggle]');
    if (!toggle) return;
    const root = document.getElementById('snize_results');
    const input = stockInput(root);
    stockFocusElement = toggle;
    if (input) {
      // Use the app's native handler so all other facets and sorting stay intact.
      input.click();
      // Mobile facets live in the app's body-level dialog and commit through Done.
      input.closest('#snize-modal-mobile-filters-dialog')?.querySelector('.snize-product-filters-show-products')?.click();
      schedule();
    } else if (toggle.getAttribute('aria-pressed') === 'true') {
      const url = new URL(location.href);
      url.searchParams.delete('rb_stock_status');
      url.searchParams.delete('page');
      const preview = selectedPreview();
      if (preview) url.searchParams.set('preview_theme_id', preview);
      location.assign(url.href);
    }
  });
  page.addEventListener('change', event => { if (event.target.matches('input[name="stock_status"]')) schedule(); });
  desktop.addEventListener('change', () => { closeFilters(); schedule(); });
  function connectResults() {
    const jq = window.Searchanise?.$ || window.jQuery || window.$;
    if (!jq) return;
    jq(document).off('Searchanise.ResultsUpdated.dacSearchLayout').on('Searchanise.ResultsUpdated.dacSearchLayout', () => {
      window.DACSearchQueryDisplay?.settled();
      window.DACSearchQueryDisplay?.cleanEmptyQuery?.();
      resultsLocation = searchLocation();
      schedule();
    });
  }
  document.addEventListener('Searchanise.Loaded', connectResults);
  connectResults();
  window.addEventListener('popstate', () => {
    const destination = searchLocation();
    if (destination === resultsLocation) return; // Keep same-search anchor navigation native.
    resultsLocation = destination;
    // The provider does not restore HTML5 history, and resize replays its old
    // results. Load the selected URL so old requests/cached facets cannot win.
    // A reload preserves the history entry, locale, query and preview session.
    location.reload();
  });
  apply();
  window.DACSearchQueryDisplay?.cleanEmptyQuery?.();
  resultsLocation = searchLocation();
  observer.observe(page, {childList:true,subtree:true});
})();
